// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/roles/RoleManagementPanel.jsx

import React, { useEffect, useMemo, useState } from "react";
import PermissionGroup from "./PermissionGroup";
import RoleCard from "./RoleCard";
import RoleDetailDrawer from "./RoleDetailDrawer";

function groupMatchesSearch(group, permissions, searchTerm) {
  const needle = String(searchTerm || "").trim().toLowerCase();
  if (!needle) return true;
  if (String(group).toLowerCase().includes(needle)) return true;

  return (permissions || []).some((permission) =>
    [
      permission.permission_label,
      permission.label,
      permission.name,
      permission.description,
      permission.permission_key,
      permission.key,
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(needle))
  );
}

export default function RoleManagementPanel({ roles = [], permissions = {} }) {
  const [selectedRoleKey, setSelectedRoleKey] = useState(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [permissionSearch, setPermissionSearch] = useState("");

  useEffect(() => {
    if (!roles.length) {
      setSelectedRoleKey(null);
      return;
    }

    if (!roles.some((role) => role.role_key === selectedRoleKey)) {
      setSelectedRoleKey(roles[0].role_key);
    }
  }, [roles, selectedRoleKey]);

  const selectedRole = useMemo(
    () =>
      roles.find((role) => role.role_key === selectedRoleKey) ||
      roles[0] ||
      null,
    [roles, selectedRoleKey]
  );

  const permissionEntries = useMemo(
    () =>
      Object.entries(permissions || {}).filter(([group, items]) =>
        groupMatchesSearch(
          group,
          Array.isArray(items) ? items : [],
          permissionSearch
        )
      ),
    [permissions, permissionSearch]
  );

  const totalPermissions = Object.values(permissions || {}).reduce(
    (count, group) => count + (Array.isArray(group) ? group.length : 0),
    0
  );
  const visiblePermissionCount = permissionEntries.reduce(
    (count, [, group]) => count + (Array.isArray(group) ? group.length : 0),
    0
  );
  const permissionGroupCount = Object.keys(permissions || {}).length;
  const assignedUserCount = roles.reduce(
    (count, role) => count + Number(role.assignment_count || 0),
    0
  );
  const protectedRoleCount = roles.filter(
    (role) => role.is_system_role && role.is_deletable === false
  ).length;

  return (
    <section className="enterprise-admin-section-panel enterprise-admin-roles-panel">
      <div className="enterprise-admin-section-header">
        <p className="enterprise-admin-eyebrow">Roles &amp; permissions</p>
        <h2>Authority model</h2>
        <p>
          Review organization roles, active assignments, and the permissions
          OASYS enforces for runtime access and administrative audit.
        </p>
      </div>

      <div className="enterprise-admin-metric-grid enterprise-admin-compact-metrics">
        <div className="enterprise-admin-metric-card">
          <span>Organization roles</span>
          <strong>{roles.length}</strong>
        </div>
        <div className="enterprise-admin-metric-card">
          <span>Permission groups</span>
          <strong>{permissionGroupCount}</strong>
        </div>
        <div className="enterprise-admin-metric-card">
          <span>Active assignments</span>
          <strong>{assignedUserCount}</strong>
        </div>
        <div className="enterprise-admin-metric-card">
          <span>Protected roles</span>
          <strong>{protectedRoleCount}</strong>
        </div>
      </div>

      <div className="enterprise-admin-two-column enterprise-admin-authority-layout">
        <section className="enterprise-admin-form-card enterprise-admin-role-catalog-card">
          <div className="enterprise-admin-card-heading">
            <p className="enterprise-admin-eyebrow">Role catalog</p>
            <h3>Organization authority levels</h3>
            <p>
              Select a role to review its purpose, assignments, and included
              permissions. Protected system roles cannot be deleted.
            </p>
          </div>

          {roles.length ? (
            <div className="enterprise-admin-role-grid compact">
              {roles.map((role) => (
                <RoleCard
                  key={role.role_key}
                  role={role}
                  active={selectedRole?.role_key === role.role_key}
                  onSelect={(nextRole) => setSelectedRoleKey(nextRole.role_key)}
                />
              ))}
            </div>
          ) : (
            <div className="enterprise-admin-empty compact">
              <strong>No organization roles available</strong>
              <p>Role definitions will appear after the authority catalog loads.</p>
            </div>
          )}
        </section>

        <RoleDetailDrawer role={selectedRole} />
      </div>

      <section
        className={`enterprise-admin-form-card enterprise-admin-permission-summary enterprise-admin-catalog-disclosure${
          catalogOpen ? " is-open" : ""
        }`}
      >
        <div className="enterprise-admin-catalog-summary-row">
          <div className="enterprise-admin-card-heading compact">
            <p className="enterprise-admin-eyebrow">Permission catalog</p>
            <h3>{totalPermissions} enforced permissions</h3>
            <p>
              Technical permission keys remain available for audit and
              implementation traceability, but stay collapsed during normal use.
            </p>
          </div>

          <button
            type="button"
            className="enterprise-admin-secondary-button"
            onClick={() => setCatalogOpen((value) => !value)}
            aria-expanded={catalogOpen}
            aria-controls="enterprise-admin-permission-catalog"
          >
            {catalogOpen ? "Hide permission catalog" : "View permission catalog"}
          </button>
        </div>

        {catalogOpen ? (
          <div
            id="enterprise-admin-permission-catalog"
            className="enterprise-admin-catalog-expanded"
          >
            <label className="enterprise-admin-search-label">
              <span>Search permissions</span>
              <input
                type="search"
                value={permissionSearch}
                onChange={(event) => setPermissionSearch(event.target.value)}
                placeholder="Search permissions, descriptions, groups, or keys"
              />
            </label>

            <div className="enterprise-admin-catalog-results">
              <span>
                {visiblePermissionCount} permission
                {visiblePermissionCount === 1 ? "" : "s"} in{" "}
                {permissionEntries.length} group
                {permissionEntries.length === 1 ? "" : "s"}
              </span>
              {permissionSearch ? (
                <button
                  type="button"
                  className="enterprise-admin-catalog-clear"
                  onClick={() => setPermissionSearch("")}
                >
                  Clear search
                </button>
              ) : null}
            </div>

            <div className="enterprise-admin-permission-stack enterprise-admin-catalog-accordion-grid">
              {permissionEntries.map(([group, items]) => (
                <PermissionGroup
                  key={group}
                  title={group}
                  permissions={Array.isArray(items) ? items : []}
                  searchTerm={permissionSearch}
                  collapsible
                  defaultOpen={Boolean(permissionSearch)}
                />
              ))}
            </div>

            {!permissionEntries.length ? (
              <div className="enterprise-admin-empty compact">
                <strong>No permissions match your search</strong>
                <p>Try another label, description, group, or permission key.</p>
              </div>
            ) : null}
          </div>
        ) : null}
      </section>
    </section>
  );
}
